import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const REVIEW_STATUSES = [
  'intake_submitted',
  'ready_for_attorney_review',
  'legal_triage',
  'attorney_review',
  'more_information_needed'
];

const formatSupabaseError = (err, fallback) => {
  return [err?.message, err?.details, err?.hint].filter(Boolean).join(' ') || fallback;
};

const getStatusLabel = (status) => {
  if (status === 'matter_created') return 'plan created';
  if (status === 'ready_for_attorney_review') return 'ready for attorney review';
  if (status === 'attorney_review') return 'attorney review in progress';
  if (status === 'more_information_needed') return 'waiting for additional information';
  if (status === 'attorney_review_complete') return 'attorney review complete';
  if (status === 'ready_for_document_generation') return 'ready for document generation';
  if (status === 'documents_generated') return 'documents generated';
  return status?.replace('matter', 'plan').replaceAll('_', ' ');
};

const StatusBadge = ({ status }) => {
  const colors = status === 'completed'
    ? 'bg-green-50 text-green-700 border-green-100'
    : REVIEW_STATUSES.includes(status)
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : 'bg-blue-50 text-blue-700 border-blue-100';

  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${colors}`}>
      {getStatusLabel(status)}
    </span>
  );
};

const LawFirmDashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ assigned: 0, review: 0, openTasks: 0, myTasks: 0 });
  const [recentMatters, setRecentMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!profile?.law_firm_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [assignedRes, reviewRes, openTaskRes, myTaskRes, recentRes] = await Promise.all([
          supabase
            .from('matters')
            .select('*', { count: 'exact', head: true })
            .eq('law_firm_id', profile.law_firm_id)
            .neq('status', 'completed'),
          supabase
            .from('matters')
            .select('*', { count: 'exact', head: true })
            .eq('law_firm_id', profile.law_firm_id)
            .in('status', REVIEW_STATUSES),
          supabase
            .from('matter_tasks')
            .select('*', { count: 'exact', head: true })
            .eq('law_firm_id', profile.law_firm_id)
            .neq('status', 'completed'),
          supabase
            .from('matter_tasks')
            .select('*', { count: 'exact', head: true })
            .eq('law_firm_id', profile.law_firm_id)
            .eq('assigned_to', profile.id)
            .neq('status', 'completed'),
          supabase
            .from('matters')
            .select(`
              id,
              plan_type,
              status,
              client_state,
              created_at,
              client:clients (
                first_name,
                last_name,
                email
              )
            `)
            .eq('law_firm_id', profile.law_firm_id)
            .order('created_at', { ascending: false })
            .limit(6)
        ]);

        if (assignedRes.error) throw assignedRes.error;
        if (reviewRes.error) throw reviewRes.error;
        if (openTaskRes.error) throw openTaskRes.error;
        if (myTaskRes.error) throw myTaskRes.error;
        if (recentRes.error) throw recentRes.error;

        setStats({
          assigned: assignedRes.count || 0,
          review: reviewRes.count || 0,
          openTasks: openTaskRes.count || 0,
          myTasks: myTaskRes.count || 0
        });
        setRecentMatters(recentRes.data || []);
      } catch (err) {
        console.error('Law firm dashboard error:', err);
        setError(formatSupabaseError(err, 'Unable to load law firm dashboard.'));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [profile]);

  if (!profile?.law_firm_id) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
        <SafeIcon icon={FiIcons.FiAlertTriangle} className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-900">Law firm profile incomplete</h1>
        <p className="text-sm text-slate-500 mt-2">Your profile is missing a law firm assignment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Law Firm Portal</h1>
          <p className="mt-1 text-sm text-slate-500">Welcome, {profile?.first_name} {profile?.last_name}</p>
        </div>
        <button
          onClick={() => navigate('/law-firm/matters')}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiFolder} className="mr-2 h-4 w-4" />
          View Assigned Plans
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Assigned Plans', value: stats.assigned, icon: FiIcons.FiInbox, bg: 'bg-blue-50', color: 'text-blue-600', path: '/law-firm/matters' },
          { label: 'Plans Needing Review', value: stats.review, icon: FiIcons.FiFileText, bg: 'bg-amber-50', color: 'text-amber-600', path: '/law-firm/matters' },
          { label: 'Open Tasks', value: stats.openTasks, icon: FiIcons.FiCheckSquare, bg: 'bg-slate-50', color: 'text-slate-600', path: '/law-firm/tasks' },
          { label: 'My Tasks', value: stats.myTasks, icon: FiIcons.FiUserCheck, bg: 'bg-green-50', color: 'text-green-600', path: '/law-firm/tasks' },
        ].map((item) => (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className="bg-white overflow-hidden shadow rounded-lg border border-slate-200 text-left hover:border-slate-300 hover:shadow-md transition-all"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 ${item.bg} rounded-md p-3`}>
                  <SafeIcon icon={item.icon} className={`h-6 w-6 ${item.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">{item.label}</dt>
                    <dd className="text-2xl font-semibold text-slate-900">{loading ? '...' : item.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white shadow rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">Recent Assigned Plans</h3>
          <button onClick={() => navigate('/law-firm/matters')} className="text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-widest">
            View All
          </button>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-300">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 mx-auto" />
          </div>
        ) : recentMatters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Created</th>
                  <th className="px-6 py-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentMatters.map((matter) => (
                  <tr key={matter.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900">{matter.client?.first_name} {matter.client?.last_name}</div>
                      <div className="text-xs text-slate-500">{matter.client?.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-600 font-medium">{matter.plan_type}</div>
                      <div className="text-xs text-slate-400">{matter.client_state || 'State not set'}</div>
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={matter.status} /></td>
                    <td className="px-6 py-4 text-sm text-slate-500">{format(new Date(matter.created_at), 'MMM d, yyyy')}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => navigate(`/law-firm/matters/${matter.id}`)}
                        className="p-1 px-3 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded transition-colors"
                      >
                        VIEW
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-slate-400">
            <SafeIcon icon={FiIcons.FiInbox} className="h-10 w-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm">No plans have been assigned to your law firm yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawFirmDashboard;
